import { useEffect, useRef, useState } from 'react';
import * as maptilersdk from '@maptiler/sdk';
import '@maptiler/sdk/dist/maptiler-sdk.css';

// Your free MapTiler API key - get from https://cloud.maptiler.com/account/keys/
const MAPTILER_API_KEY = 'WjPBhtYok4RcJ6nyTxlF'; // Replace with your actual key

function MapTilerView({ venue, onClose }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coordinates, setCoordinates] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'satellite'

  // Initialize MapTiler SDK
  useEffect(() => {
    maptilersdk.config.apiKey = MAPTILER_API_KEY;
  }, []);

  // Geocode the venue using MapTiler's geocoding (no CORS issues!)
  useEffect(() => {
    const geocodeVenue = async () => {
      if (!venue) {
        setError('No venue provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log("Geocoding venue:", venue);

        // Use MapTiler geocoding API (free, no CORS)
        const response = await fetch(
          `https://api.maptiler.com/geocoding/${encodeURIComponent(venue)}.json?key=${MAPTILER_API_KEY}&language=en&limit=1`
        );
        
        if (!response.ok) {
          throw new Error(`Geocoding failed: ${response.status}`);
        }
        
        const data = await response.json();
        console.log("Geocoding response:", data);
        
        if (data.features && data.features.length > 0) {
          const feature = data.features[0];
          const [lng, lat] = feature.center;
          
          setCoordinates({
            lat,
            lng,
            displayName: feature.place_name
          });
          setError(null);
        } else {
          // Fallback to Hyderabad
          setCoordinates({
            lat: 17.3850,
            lng: 78.4867,
            displayName: 'Hyderabad, India (Default)'
          });
          setError('Location not found. Showing default location.');
        }
      } catch (err) {
        console.error('Geocoding error:', err);
        setError('Failed to load map. Showing default location.');
        setCoordinates({
          lat: 17.3850,
          lng: 78.4867,
          displayName: 'Hyderabad, India (Default)'
        });
      } finally {
        setLoading(false);
      }
    };

    geocodeVenue();
  }, [venue]);

  // Initialize map when coordinates are available
  useEffect(() => {
    if (!coordinates || map.current) return;

    map.current = new maptilersdk.Map({
      container: mapContainer.current,
      style: viewMode === 'satellite' 
        ? maptilersdk.MapStyle.SATELLITE 
        : maptilersdk.MapStyle.STREETS,
      center: [coordinates.lng, coordinates.lat],
      zoom: 16,
      pitch: viewMode === '3d' ? 60 : 0,
      bearing: 0,
      navigationControl: true,
      geolocateControl: true,
      fullscreenControl: true
    });

    // Add 3D buildings
    map.current.on('load', () => {
      // Add marker
      new maptilersdk.Marker({ color: '#FF4444' })
        .setLngLat([coordinates.lng, coordinates.lat])
        .setPopup(new maptilersdk.Popup().setHTML(`
          <div style="text-align: center; padding: 8px;">
            <strong style="color: #333; font-size: 14px;">${venue}</strong><br>
            <span style="color: #666; font-size: 12px;">${coordinates.displayName || ''}</span>
          </div>
        `))
        .addTo(map.current);

      // Add 3D buildings layer
      map.current.addLayer({
        'id': '3d-buildings',
        'source': 'openmaptiles',
        'source-layer': 'building',
        'type': 'fill-extrusion',
        'minzoom': 15,
        'filter': ['==', 'render_height', 0],
        'paint': {
          'fill-extrusion-color': [
            'interpolate',
            ['linear'],
            ['get', 'render_height'],
            0, '#f2e6d5',
            10, '#e6d5b8',
            50, '#d9c4a3'
          ],
          'fill-extrusion-height': [
            'interpolate',
            ['linear'],
            ['zoom'],
            15, 0,
            16, ['get', 'render_height']
          ],
          'fill-extrusion-base': ['get', 'render_min_height'],
          'fill-extrusion-opacity': 0.8
        }
      });
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [coordinates, viewMode]);

  // Handle view mode changes
  const toggleView = (mode) => {
    if (!map.current || !coordinates) return;
    
    setViewMode(mode);
    
    if (mode === 'satellite') {
      map.current.setStyle(maptilersdk.MapStyle.SATELLITE);
    } else if (mode === '3d') {
      map.current.setStyle(maptilersdk.MapStyle.STREETS);
      map.current.flyTo({
        center: [coordinates.lng, coordinates.lat],
        zoom: 18,
        pitch: 75,
        bearing: 30,
        duration: 2000
      });
    } else {
      map.current.setStyle(maptilersdk.MapStyle.STREETS);
      map.current.flyTo({
        center: [coordinates.lng, coordinates.lat],
        zoom: 16,
        pitch: 0,
        bearing: 0,
        duration: 2000
      });
    }
  };

  return (
    <div className="map-overlay" onClick={onClose}>
      <div className="map-content" onClick={(e) => e.stopPropagation()}>
        <button className="map-close" onClick={onClose}>×</button>
        
        <div className="map-header">
          <h3>
            <span className="header-icon">📍</span>
            {venue}
          </h3>
          <div className="view-controls">
            <button 
              className={`view-btn ${viewMode === 'map' ? 'active' : ''}`}
              onClick={() => toggleView('map')}
            >
              🗺️ Map
            </button>
            <button 
              className={`view-btn ${viewMode === 'satellite' ? 'active' : ''}`}
              onClick={() => toggleView('satellite')}
            >
              🛰️ Satellite
            </button>
            <button 
              className={`view-btn ${viewMode === '3d' ? 'active' : ''}`}
              onClick={() => toggleView('3d')}
            >
              🏙️ 3D
            </button>
          </div>
        </div>

        {loading && (
          <div className="map-loading">
            <div className="spinner"></div>
            <p>Loading map...</p>
          </div>
        )}

        {error && !loading && (
          <div className="map-error">
            <p>⚠️ {error}</p>
          </div>
        )}

        <div 
          ref={mapContainer} 
          className="map-container"
          style={{ 
            width: '100%', 
            height: '450px',
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.3s'
          }} 
        />

        <div className="map-footer">
          <p className="attribution">
            © <a href="https://www.maptiler.com/" target="_blank" rel="noopener noreferrer">MapTiler</a> | 
            © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors
          </p>
          <button className="done-btn" onClick={onClose}>Done</button>
        </div>
      </div>

      <style jsx>{`
        .map-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.3s ease;
        }

        .map-content {
          background: white;
          border-radius: 20px;
          width: 90%;
          max-width: 1000px;
          max-height: 90vh;
          overflow: hidden;
          position: relative;
          animation: slideUp 0.3s ease;
          box-shadow: 0 30px 60px rgba(0,0,0,0.4);
        }

        .map-close {
          position: absolute;
          top: 15px;
          right: 15px;
          background: white;
          border: none;
          font-size: 28px;
          cursor: pointer;
          color: #666;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.3s;
          z-index: 10;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .map-close:hover {
          background: #f0f0f0;
          transform: rotate(90deg);
        }

        .map-header {
          padding: 20px 25px;
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 15px;
        }

        .map-header h3 {
          margin: 0;
          font-size: 1.3rem;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .header-icon {
          font-size: 1.8rem;
        }

        .view-controls {
          display: flex;
          gap: 8px;
          background: rgba(255,255,255,0.1);
          padding: 5px;
          border-radius: 10px;
        }

        .view-btn {
          background: transparent;
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .view-btn:hover {
          background: rgba(255,255,255,0.2);
        }

        .view-btn.active {
          background: white;
          color: #1e3c72;
        }

        .map-loading {
          padding: 100px;
          text-align: center;
          color: #666;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #1e3c72;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        .map-error {
          padding: 30px;
          text-align: center;
          background: #fff3f3;
          color: #d63031;
        }

        .map-footer {
          padding: 15px 25px;
          background: #f8f9fa;
          border-top: 1px solid #eee;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .attribution {
          margin: 0;
          color: #666;
          font-size: 0.8rem;
        }

        .attribution a {
          color: #1e3c72;
          text-decoration: none;
        }

        .attribution a:hover {
          text-decoration: underline;
        }

        .done-btn {
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          color: white;
          border: none;
          padding: 8px 30px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .done-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 20px rgba(30, 60, 114, 0.4);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default MapTilerView;